import React, {FC} from 'react';
import {styled} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import MessagePillArrow from '../../../../assets/images/basicsGuide/messagePillArrow.svg';
import Drew from '../../../../assets/images/drew.svg';
import Kendal from '../../../../assets/images/kendal.svg';
import Sam from '../../../../assets/images/sam.svg';
import Shahine from '../../../../assets/images/shahine.svg';
import {onboardingGuideRightSideWidth, onboardingGuideSlideHeight} from '../../guide/onboardingGuideHelpers';
import {
  CardText,
  StyledInboxCard,
  StyledInboxIcon,
  StyledVisualContainer,
  StyledVisualLayout,
} from './onboardingBasicsGuideHelpers';

/*
 * Props.
 */

interface OnboardingBasicsGuideBestPracticesVisualProps {}

/*
 * Style.
 */

const StyledBestPracticesVisualContainer = styled.div`
  position: absolute;
  width: ${onboardingGuideRightSideWidth};
  height: ${onboardingGuideSlideHeight};
`;

const StyledBestPracticesContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 32px;
`;

const StyledBestPracticesItemContainer = styled.div`
  display: flex;
  align-items: center;
  justify-contenr: center;
  flex-direction: column;
  gap: 20px;
`;

const StyledBestPracticesDetailsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-contenr: center;
  flex-direction: column;
  gap: 4px;
`;

const StyledInboxesVisualContainer = styled.div`
  display: flex;
  align-items: center;
  justify-contenr: center;
  flex-direction: row;
  gap: 16px;
`;

const StyledCardText = styled(CardText)`
  font-weight: ${fontWeights.normal};
`;

const StyledBestPracticesInboxIcon = styled(StyledInboxIcon)`
  transform: scale(${32 / 20});
  height: 32px;
  margin: 18px 0 12px 0;
`;

const StyledBillingInboxIcon = styled(StyledBestPracticesInboxIcon)`
  color: #006eb2;
`;

const StyledItInboxIcon = styled(StyledBestPracticesInboxIcon)`
  color: #2d8058;
`;

const StyledSupportEmailsInboxIcon = styled(StyledBestPracticesInboxIcon)`
  color: #808080;
`;

const StyledSupportChatInboxIcon = styled(StyledBestPracticesInboxIcon)`
  color: #808080;
`;

const StyledSupportGeneralInboxIcon = styled(StyledBestPracticesInboxIcon)`
  color: #006eb2;
`;

const StyledSupportPriorityInboxIcon = styled(StyledBestPracticesInboxIcon)`
  color: #dd3c00;
`;

const StyledTeammatesPill = styled.div`
  width: 68px;
  height: 26px;
  border-radius: 20px;
  color: ${(p) => p.theme.palette.blue.shade50};
  background: ${(p) => p.theme.greys.white};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  line-height: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
  transform: rotate(-8deg);
`;

const StyledTeammatePlaceholder = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 8px;
  background: ${(p) => p.theme.greys.shade30};
`;

const StyledMessagePillContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  transform: rotate(-8deg);
  gap: 4px;
`;

const StyledMessagePill = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
  background: ${(p) => p.theme.greys.white};
`;

const StyledMessageIcon = styled(Icon)`
  color: #808080;
`;

const StyledSupportEmailsMessageIcon = styled(StyledMessageIcon)`
  color: #006eb2;
`;

const StyledSupportChatMessageIcon = styled(StyledMessageIcon)`
  color: #2d8058;
`;

const StyledMessagePillArrow = styled.img`
  transform: rotate(8deg);
`;

const StyledTagIcon = styled(Icon)`
  transform: scale(${8 / 16});
`;

const StyledTeamInboxesVisualContainer = styled(StyledInboxesVisualContainer)`
  ${computeFadeInAnimation('teamInboxesVisualContainerFadeIn', 200, 400)}
`;

const StyledBillingTeammatesPill = styled(StyledTeammatesPill)`
  position: absolute;
  z-index: 1;
  top: 156px;
  left: 106px;

  ${computeFadeInAnimation('billingTeammatesBestPracticesFadeIn', 200, 400)}
`;

const StyledItTeammatesPill = styled(StyledTeammatesPill)`
  position: absolute;
  z-index: 1;
  top: 156px;
  left: 270px;

  ${computeFadeInAnimation('itTeammatesBestPracticesFadeIn', 200, 400)}
`;

const StyledChannelInboxesVisualContainer = styled(StyledInboxesVisualContainer)`
  ${computeFadeInAnimation('channelInboxesVisualContainerFadeIn', 200, 400)}
`;

const StyledSupportEmailsMessagePillContainer = styled(StyledMessagePillContainer)`
  position: absolute;
  z-index: 1;
  top: 330px;
  left: 106px;

  ${computeFadeInAnimation('supportEmailsMessageBestPracticesFadeIn', 200, 400)}
`;

const StyledSupportChatMessagePillContainer = styled(StyledMessagePillContainer)`
  position: absolute;
  z-index: 1;
  top: 330px;
  left: 270px;

  ${computeFadeInAnimation('supportChatMessageBestPracticesFadeIn', 200, 400)}
`;

const StyledPriorityInboxesVisualContainer = styled(StyledInboxesVisualContainer)`
  ${computeFadeInAnimation('priorityInboxesVisualContainerFadeIn', 200, 400)}
`;

const StyledSupportGeneralMessagePillContainer = styled(StyledMessagePillContainer)`
  position: absolute;
  z-index: 1;
  top: 508px;
  left: 106px;

  ${computeFadeInAnimation('tagRuleBestPracticesFadeIn', 200, 400)}
`;

const StyledSupportPriorityMessagePillContainer = styled(StyledMessagePillContainer)`
  position: absolute;
  z-index: 1;
  top: 508px;
  left: 270px;

  ${computeFadeInAnimation('tagRuleBestPracticesFadeIn', 200, 400)}
`;

const StyledAnimatedMessagePill = styled(StyledMessagePill)`
  ${computeLuminescentAnimation('animatedMessagePillGlow', 200, 400)}
`;

const StyledAnimatedPriorityMessagePill = styled(StyledMessagePill)`
  ${computeLuminescentAnimation('animatedPriorityMessagePillGlow', 200, 400, true)}
`;

const StyledTagRulePill = styled.div`
  width: 34px;
  height: 12px;
  border-radius: 4px;
  color: #dd3c00;
  background: #ffd2b3;
  font-size: 8px;
  font-weight: ${fontWeights.bold};
  line-height: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  transform: rotate(-8deg);
  position: absolute;
  z-index: 2;
  top: 535px;
  left: 272px;

  ${computeFadeInAnimation('tagRuleBestPracticesFadeIn', 200, 400)}
`;

const StyledBestPracticesHeaderText = styled.div`
  margin-top: 62px;
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.bold};
  line-height: 18px;
  text-align: center;
  color: ${(p) => p.theme.greys.shade80};
  margin-bottom: 24px;

  ${computeFadeInAnimation('bestPracticesFadeIn', 200, 400)}
`;

const StyledBestPracticesDetailsHeaderText = styled.div`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.bold};
  line-height: 18px;
  text-align: center;
  color: ${(p) => p.theme.greys.shade80};

  ${computeFadeInAnimation('bestPracticesDetailsHeaderTextFadeIn', 200, 400)}
`;

const StyledBestPracticesDetailsText = styled.div`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.normal};
  line-height: 14px;
  text-align: center;
  color: ${(p) => p.theme.greys.shade70};

  ${computeFadeInAnimation('bestPracticesDetailsTextFadeIn', 200, 400)}
`;

/*
 * Component.
 */

export const OnboardingBasicsGuideBestPracticesVisual: FC<
  OnboardingBasicsGuideBestPracticesVisualProps
> = () => (
  <StyledVisualContainer>
    <StyledBestPracticesVisualContainer>
      <StyledBillingTeammatesPill>
        <img src={Shahine} alt="Shahine" />
        <img src={Drew} alt="Drew" />
        <StyledTeammatePlaceholder />
      </StyledBillingTeammatesPill>
      <StyledItTeammatesPill>
        <img src={Kendal} alt="Kendal" />
        <img src={Sam} alt="Sam" />
        <StyledTeammatePlaceholder />
      </StyledItTeammatesPill>
      <StyledSupportEmailsMessagePillContainer>
        <StyledMessagePill>
          <StyledSupportEmailsMessageIcon name="channelEmailSmall" />
        </StyledMessagePill>
        <StyledMessagePillArrow src={MessagePillArrow} alt="Oraganize your inbox with Front!" />
      </StyledSupportEmailsMessagePillContainer>
      <StyledSupportChatMessagePillContainer>
        <StyledMessagePill>
          <StyledSupportChatMessageIcon name="channelChatSmall" />
        </StyledMessagePill>
        <StyledMessagePillArrow src={MessagePillArrow} alt="Oraganize your inbox with Front!" />
      </StyledSupportChatMessagePillContainer>
      <StyledSupportGeneralMessagePillContainer>
        <StyledAnimatedMessagePill>
          <StyledMessageIcon name="channelEmailSmall" />
        </StyledAnimatedMessagePill>
        <StyledMessagePillArrow src={MessagePillArrow} alt="Oraganize your inbox with Front!" />
      </StyledSupportGeneralMessagePillContainer>
      <StyledTagRulePill>
        <StyledTagIcon name="tagFilledSmall" /> VIP
      </StyledTagRulePill>
      <StyledSupportPriorityMessagePillContainer>
        <StyledAnimatedPriorityMessagePill>
          <StyledMessageIcon name="channelEmailSmall" />
        </StyledAnimatedPriorityMessagePill>
        <StyledMessagePillArrow src={MessagePillArrow} alt="Oraganize your inbox with Front!" />
      </StyledSupportPriorityMessagePillContainer>
      <StyledVisualLayout>
        <StyledBestPracticesHeaderText>
          <Trans id="onboarding-basics-guide-best-practices-visual-inbox-organization">
            Common and effective inbox organization
          </Trans>
        </StyledBestPracticesHeaderText>
        <StyledBestPracticesContainer>
          <StyledBestPracticesItemContainer>
            <StyledBestPracticesDetailsContainer>
              <StyledBestPracticesDetailsHeaderText>
                <Trans id="onboarding-basics-guide-best-practices-visual-by-team-header">By team</Trans>
              </StyledBestPracticesDetailsHeaderText>
              <StyledBestPracticesDetailsText>
                <Trans id="onboarding-basics-guide-best-practices-visual-by-team-details">
                  Organize inboxes by teams to ensure the right work queues
                </Trans>
              </StyledBestPracticesDetailsText>
            </StyledBestPracticesDetailsContainer>
            <StyledTeamInboxesVisualContainer>
              <StyledInboxCard>
                <StyledBillingInboxIcon name="inboxFilled" />
                <StyledCardText>Billing</StyledCardText>
              </StyledInboxCard>
              <StyledInboxCard>
                <StyledItInboxIcon name="inboxFilled" />
                <StyledCardText>IT</StyledCardText>
              </StyledInboxCard>
            </StyledTeamInboxesVisualContainer>
          </StyledBestPracticesItemContainer>
          <StyledBestPracticesItemContainer>
            <StyledBestPracticesDetailsContainer>
              <StyledBestPracticesDetailsHeaderText>
                <Trans id="onboarding-basics-guide-best-practices-visual-by-channel-type-header">
                  By channel type
                </Trans>
              </StyledBestPracticesDetailsHeaderText>
              <StyledBestPracticesDetailsText>
                <Trans id="onboarding-basics-guide-best-practices-visual-by-channel-type-details">
                  Separate emails from live chats to achieve different response time goals
                </Trans>
              </StyledBestPracticesDetailsText>
            </StyledBestPracticesDetailsContainer>
            <StyledChannelInboxesVisualContainer>
              <StyledInboxCard>
                <StyledSupportEmailsInboxIcon name="inboxFilled" />
                <StyledCardText>Support Emails</StyledCardText>
              </StyledInboxCard>
              <StyledInboxCard>
                <StyledSupportChatInboxIcon name="inboxFilled" />
                <StyledCardText>Support Chat</StyledCardText>
              </StyledInboxCard>
            </StyledChannelInboxesVisualContainer>
          </StyledBestPracticesItemContainer>
          <StyledBestPracticesItemContainer>
            <StyledBestPracticesDetailsContainer>
              <StyledBestPracticesDetailsHeaderText>
                <Trans id="onboarding-basics-guide-best-practices-visual-by-priority-header">
                  By priority
                </Trans>
              </StyledBestPracticesDetailsHeaderText>
              <StyledBestPracticesDetailsText>
                <Trans id="onboarding-basics-guide-best-practices-visual-by-priority-details">
                  Create escalation inboxes for messages that require special attention
                </Trans>
              </StyledBestPracticesDetailsText>
            </StyledBestPracticesDetailsContainer>
            <StyledPriorityInboxesVisualContainer>
              <StyledInboxCard>
                <StyledSupportGeneralInboxIcon name="inboxFilled" />
                <StyledCardText>Support General</StyledCardText>
              </StyledInboxCard>
              <StyledInboxCard>
                <StyledSupportPriorityInboxIcon name="inboxFilled" />
                <StyledCardText>Support Priority</StyledCardText>
              </StyledInboxCard>
            </StyledPriorityInboxesVisualContainer>
          </StyledBestPracticesItemContainer>
        </StyledBestPracticesContainer>
      </StyledVisualLayout>
    </StyledBestPracticesVisualContainer>
  </StyledVisualContainer>
);

function computeFadeInAnimation(animationName: string, delayMs: number, durationMs: number) {
  return `
    animation-name: ${animationName};
    animation-delay: ${delayMs}ms;
    animation-duration: ${durationMs}ms;
    animation-fill-mode: forwards;
    opacity: 0;
    @keyframes ${animationName} {
      0% {
        opacity: 0;
      }
      100% {
        opacity: 1;
      }
    }
  `;
}

function computeLuminescentAnimation(
  animationName: string,
  delayMs: number,
  durationMs: number,
  isPriority?: boolean,
) {
  return `
    animation-name: ${animationName};
    animation-delay: ${delayMs}ms;
    animation-duration: ${durationMs}ms;
    animation-fill-mode: forwards;
    box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);

    @keyframes ${animationName} {
      0% {
        box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
      }

      100% {
        box-shadow: 0px 0px 10px 0px ${isPriority ? 'rgba(255, 104, 51, 1)' : 'rgba(77, 237, 255, 1)'};
      }
    }
  `;
}
